import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { pushSupported, getPushState, enablePush, disablePush } from "@/lib/push";
import { getSoftAsk } from "@/lib/api";

const DISMISS_KEY = "readoo_softask_dismissed_at";
const DISMISS_DAYS = 3;

const Ctx = createContext(null);
export const usePush = () => useContext(Ctx) || {};

export function PushProvider({ children }) {
  const { user } = useAuth();
  const { gamificationEnabled } = useSettings();
  const [state, setState] = useState("off");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState({ title: "Attiva le notifiche", body: "Resta aggiornato e non perderti nulla.", cta: "Attiva" });

  const refresh = useCallback(async () => { setState(await getPushState()); }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { getSoftAsk().then(setCopy).catch(() => {}); }, []);

  const recentlyDismissed = () => (Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0)) < DISMISS_DAYS * 864e5;

  useEffect(() => {
    if (!user || !gamificationEnabled || !pushSupported() || state !== "off" || recentlyDismissed()) { setShow(false); return; }
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, [user, state, gamificationEnabled]);

  const promptSoftAsk = () => {
    if (!pushSupported()) { toast.error("Notifiche non supportate da questo browser"); return; }
    if (state === "denied") { toast.error("Notifiche bloccate dal browser: sbloccale dalle impostazioni del sito"); return; }
    if (state === "on") { toast.info("Notifiche già attive"); return; }
    setShow(true);
  };
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); };

  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      setState("on"); setShow(false);
      if (res && res.bonus) toast.success("Notifiche attive! +1 Readoo di oggi (da convalidare).");
      else toast.success("Notifiche attivate!");
    } catch (e) { toast.error(e.message || "Attivazione non riuscita"); }
    finally { setBusy(false); }
  };
  const unsubscribe = async () => {
    setBusy(true);
    try { await disablePush(); setState("off"); toast.success("Notifiche disattivate"); }
    catch (e) { toast.error(e.message || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <Ctx.Provider value={{ state, refresh, promptSoftAsk, subscribe, unsubscribe, supported: pushSupported(), busy }}>
      {children}
      {show && user && gamificationEnabled && state === "off" && (
        <div data-testid="softask-banner" className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:w-[380px] z-50 rounded-2xl border border-brand/40 bg-surface shadow-2xl p-4 animate-fade-up">
          <button onClick={dismiss} aria-label="Chiudi" data-testid="softask-close" className="absolute top-3 right-3 text-subtle hover:text-onSurface"><X size={16} /></button>
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-brandTertiary text-brand flex items-center justify-center shrink-0"><Bell size={18} /></span>
            <div className="min-w-0 pr-4">
              <p className="font-serif text-lg font-semibold text-onSurface leading-tight">{copy.title}</p>
              <p className="text-sm text-onSurfaceSecondary mt-0.5">{copy.body}</p>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={subscribe} disabled={busy} data-testid="softask-accept" className="px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60 transition-colors">{copy.cta}</button>
                <button onClick={dismiss} data-testid="softask-later" className="px-3 py-2 rounded-full text-sm text-subtle hover:text-onSurface transition-colors">Più tardi</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
